<template>
  <div class="prtal">
    <EmployeeTimeline
      title="Pay range versions timeline"
      subtitle="Minimum and maximum of each version, by effective period"
      aria-label="Pay range versions timeline"
      fill-width
      :lanes="lanes"
      :legend-labels="LEGEND_LABELS"
      full-legend
      hide-today-controls
      :today="today"
      :loading="loading"
      :default-scale="defaultScale"
      :selected-pay-plan-id="selectedUid"
      :hovered-pay-plan-id="hoveredUid"
      @bar-select="onBarSelect"
      @bar-hover="onBarHover"
    />

    <section class="prtal__list" aria-labelledby="prtal-list-title">
      <header class="prtal__list-heading">
        <h2 id="prtal-list-title" class="prtal__list-title">Versions</h2>
        <p class="prtal__list-subtitle">Newest first. Future on top.</p>
      </header>

      <p v-if="!sortedVersions.length && !loading" class="prtal__empty" role="status">
        No versions yet.
      </p>

      <div v-else class="prtal__rows">
        <PayRangeVersionRowCard
          v-for="version in sortedVersions"
          :key="version.uid"
          :status="version.status"
          :state="resolveVersionPeriodState(version, today)"
          :range-code="version.rangeCode"
          :first-draft="Boolean(version.firstDraft)"
          :version-number="version.versionNumber"
          :min-amount="version.minAmount"
          :max-amount="version.maxAmount"
          :currency-code="version.currencyCode"
          :effective-from="version.effectiveFrom"
          :effective-to="version.effectiveTo"
          :reference-date="today"
          :locked="version.locked"
          :corrected="Boolean(version.corrected)"
          :corrected-at="version.correctedAt || null"
          :corrected-by-name="version.correctedByName || ''"
          :read-only="readOnly"
          :selected="selectedUid === version.uid"
          :interaction="hoveredUid === version.uid ? 'hover' : 'default'"
          @select="selectVersion(version)"
          @hover="(payload) => onRowHover(version, payload)"
          @menu-action="(item) => emit('menu-action', version, item?.value ?? item)"
          @open-chatter="emit('open-chatter', version)"
        />
      </div>
    </section>
  </div>
</template>

<script setup>
import { computed, ref } from "vue";

import EmployeeTimeline from "@components/EmployeeTimeline/EmployeeTimeline.vue";
import PayRangeVersionRowCard from "@components/PayRangeVersionRowCard/PayRangeVersionRowCard.vue";
import {
  buildTimelineLanes,
  flattenRangeVersions,
  localTodayIso,
  resolveVersionPeriodState,
  sortVersionsNewestFirst,
} from "./payRangeTimelineUtils";

const LEGEND_LABELS = { draft: "Draft" };

const props = defineProps({
  /** `[{ id, code, currencyCode, versions: [{ id, versionNumber, status, effectiveFrom, effectiveTo, minAmount, maxAmount, locked }] }]` */
  payRanges: { type: Array, default: () => [] },
  today: { type: String, default: () => localTodayIso() },
  /** Retired / archived pay range: every version is read-only (no row menus). */
  readOnly: { type: Boolean, default: false },
  selectedVersionId: { type: [String, Number], default: null },
  loading: { type: Boolean, default: false },
  defaultScale: { type: String, default: "years" },
});

const emit = defineEmits(["version-select", "version-hover", "menu-action", "open-chatter"]);

const hoveredUid = ref(null);

const allVersions = computed(() => flattenRangeVersions(props.payRanges));
const sortedVersions = computed(() => sortVersionsNewestFirst(allVersions.value));
const lanes = computed(() => buildTimelineLanes(props.payRanges, props.today));

const selectedUid = computed(() => {
  if (props.selectedVersionId == null) return null;
  const match = allVersions.value.find(
    (version) =>
      String(version.uid) === String(props.selectedVersionId) ||
      String(version.id) === String(props.selectedVersionId)
  );
  return match?.uid ?? null;
});

function findByUid(uid) {
  return allVersions.value.find((version) => String(version.uid) === String(uid)) || null;
}

function selectVersion(version) {
  emit("version-select", version);
}

function onBarSelect(uid) {
  const version = findByUid(uid);
  if (version) selectVersion(version);
}

function onBarHover(uid) {
  hoveredUid.value = uid ?? null;
  emit("version-hover", findByUid(uid));
}

function onRowHover(version, payload) {
  hoveredUid.value = payload?.hovered ? version.uid : null;
  emit("version-hover", payload?.hovered ? version : null);
}
</script>

<style scoped>
.prtal {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
}

.prtal__list {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  padding: 14px;
  border: 1px solid #eaecf0;
  border-radius: 8px;
  background: #ffffff;
}

.prtal__list-heading {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.prtal__list-title {
  margin: 0;
  font-family: Inter, sans-serif;
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  color: #101828;
}

.prtal__list-subtitle {
  margin: 0;
  font-family: Inter, sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 14.52px;
  color: #667085;
}

.prtal__empty {
  margin: 0;
  padding: 16px 0;
  font-family: Inter, sans-serif;
  font-size: 14px;
  line-height: 20px;
  color: #475467;
  text-align: center;
}

.prtal__rows {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
}
</style>
